/**
 * Owns which Studio panel is currently visible. Each panel registers
 * itself with a stable id (matching what the LLM uses in
 * `[OPEN:panel_id]` directives) and the manager swaps DOM ownership
 * when `open(id)` is called.
 *
 * Panels are mounted lazily on first open — keeps the initial render
 * cheap when the user lands on the checklist and never visits some
 * sub-panels.
 */

import type { BasePanel } from "./panels/BasePanel";

// NOTE (G2/G8): the standalone Casting, Sound, and Camera panels were
// dropped in the studio rebuild. Casting is auto-derived from the script;
// background score moved to the Timeline; the camera-angle adjuster moved
// to a per-frame Storyboard tool. Their store slices remain for back-compat
// but no panel id / registry entry / sidebar tool exists for them.
export type StudioPanelId =
  | "script_upload"
  | "timeline"
  | "script_editor"
  | "characters"
  | "assets"
  | "moodboard"
  | "location"
  | "storyboard"
  | "final_video";

export const STUDIO_PANEL_IDS: readonly StudioPanelId[] = [
  "script_upload",
  "timeline",
  "script_editor",
  "characters",
  "assets",
  "moodboard",
  "location",
  "storyboard",
  "final_video",
];

export interface StudioPanelMeta {
  id: StudioPanelId;
  /** Human-readable title shown in the panel header. */
  title: string;
  /** One-line description used by the checklist + system prompt. */
  description: string;
}

export const PANEL_REGISTRY: Record<StudioPanelId, StudioPanelMeta> = {
  script_upload: {
    id: "script_upload",
    title: "Upload Script",
    description: "Drop in a PDF script (or skip and write scenes yourself).",
  },
  timeline: {
    id: "timeline",
    title: "Scene Timeline",
    description:
      "Const navigator across BGM, SFX, dialogue, storyboard, and video beats.",
  },
  script_editor: {
    id: "script_editor",
    title: "Final Draft Script",
    description:
      "Playable screenplay view with per-line clips, character tags, and inline edits.",
  },
  characters: {
    id: "characters",
    title: "Character Creator",
    description: "Define each character: name + appearance description.",
  },
  assets: {
    id: "assets",
    title: "AI Assets",
    description: "Generate props and set pieces with multi-angle previews.",
  },
  moodboard: {
    id: "moodboard",
    title: "Mood Board / Treatment",
    description:
      "Figma-style visual direction board for films and lyric-anchored music treatments.",
  },
  location: {
    id: "location",
    title: "Location Scouting",
    description: "AI-assisted location discovery and per-scene location assignment.",
  },
  storyboard: {
    id: "storyboard",
    title: "Storyboard",
    description: "Per-scene shot panels. Depends on script + casting + locations.",
  },
  final_video: {
    id: "final_video",
    title: "Final Video",
    description: "Render and review the finished cut.",
  },
};

type PanelChangeListener = (next: StudioPanelId, previous: StudioPanelId | null) => void;

export class PanelManager {
  private readonly host: HTMLElement;
  private readonly panels = new Map<StudioPanelId, BasePanel>();
  private readonly mountedPanelIds = new Set<StudioPanelId>();
  private readonly externalOpeners = new Map<StudioPanelId, () => void>();
  private readonly listeners = new Set<PanelChangeListener>();
  private currentPanelId: StudioPanelId | null = null;

  /**
   * @param host  The container DOM element where panels are mounted
   *              (the studio shell's main content area).
   */
  constructor(host: HTMLElement) {
    this.host = host;
  }

  register(id: StudioPanelId, panel: BasePanel): void {
    this.panels.set(id, panel);
  }

  /**
   * Register a handler for a panel id that isn't a normal swappable
   * panel (it lives elsewhere in the chrome). `open(id)` delegates to
   * this handler instead of mounting into the host — used for the
   * script editor, which is now a persistent toggleable column.
   *
   * External openers don't change `currentPanelId` or fire panel-change
   * listeners: the side surface opens *alongside* whatever main panel is
   * active, so the active-tool highlight should stay put.
   */
  registerExternalOpener(id: StudioPanelId, open: () => void): void {
    this.externalOpeners.set(id, open);
  }

  /**
   * Show the named panel with a quart-out fade-up. The previous
   * panel is hidden, the new panel mounts (lazily on first open),
   * and CSS does the visual transition.
   *
   * If the id is unknown (LLM hallucinated a panel name) we log +
   * ignore rather than crash.
   */
  open(panelId: StudioPanelId): void {
    const targetPanel = this.panels.get(panelId);
    if (!targetPanel) {
      const externalOpener = this.externalOpeners.get(panelId);
      if (externalOpener) {
        externalOpener();
        return;
      }
      console.warn(`[studio] open(): unknown panel id "${panelId}"`);
      return;
    }
    if (this.currentPanelId === panelId) {
      // Already on it — still call show() so the panel can re-pull
      // any state it cares about (e.g. the checklist re-rendering).
      targetPanel.show();
      return;
    }

    const previousPanelId = this.currentPanelId;
    const previousPanel = previousPanelId
      ? this.panels.get(previousPanelId)
      : null;

    previousPanel?.hide();

    if (!this.mountedPanelIds.has(panelId)) {
      targetPanel.mount(this.host);
      this.mountedPanelIds.add(panelId);
    }
    // Two-frame entry: mark as `is-entering` (opacity 0, translated)
    // for one frame, then `show()` removes the class and CSS transitions
    // run. Without the rAF the browser batches the class swap into a
    // single frame and skips the fade.
    targetPanel.setEntering();
    this.currentPanelId = panelId;
    this.notify(panelId, previousPanelId);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => targetPanel.show());
    });
  }

  getCurrentPanelId(): StudioPanelId | null {
    return this.currentPanelId;
  }

  subscribe(listener: PanelChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  destroy(): void {
    for (const panel of this.panels.values()) {
      try {
        panel.destroy();
      } catch (error) {
        console.warn("[studio] panel destroy threw:", error);
      }
    }
    this.panels.clear();
    this.mountedPanelIds.clear();
    this.listeners.clear();
    this.currentPanelId = null;
  }

  private notify(next: StudioPanelId, previous: StudioPanelId | null): void {
    this.listeners.forEach((listener) => {
      try {
        listener(next, previous);
      } catch (error) {
        console.warn("[studio] panel-change listener threw:", error);
      }
    });
  }
}
